import { fabric } from "fabric";
import { CanvasHandler } from "../../../handlers";
import store from "../../../redux/store";
import { ActiveSelectionPropertiesEnum, DirectionEnum, ObjectSuperTypesEnum, ObjectTypesEnum, SelectionTypeEnum } from "../../../enums";
import { RowLabelHelper } from "../helpers/RowLabelHelper";
import { RowSeatLabelHelper } from "../helpers";
import { selectRowTableDistribution } from "../../../utils/RowTableDistributionUtil";
import { RectTableSeatHelper } from "../helpers/RectTableSeatHelper";
import { RoundTableSeatHelper } from "../helpers/RoundTableSeatHelper";
import { RowSeatHelper } from "../helpers/RowSeatHelper";
import { RowSpacingHelper } from "../helpers/RowSpacingHelper";

const tableTypes = [
  ObjectSuperTypesEnum.RectTable,
  ObjectSuperTypesEnum.RoundTable,
]

const validTypes = [
  ObjectSuperTypesEnum.Row,
  ...tableTypes
];

const labelPropertyTypes = [
  ActiveSelectionPropertiesEnum.RowLabel,
  ActiveSelectionPropertiesEnum.TableLabel,
  ActiveSelectionPropertiesEnum.RowTableStartsAt
];
class ActiveSelectionProperties {
  private LABEL_OFFSET = 15;
  public canvasHandler: CanvasHandler;
  public handlers: any;

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
    this.handlers = {
      [ActiveSelectionPropertiesEnum.Categories]: this.updateCategories,
      [ActiveSelectionPropertiesEnum.DisplayedSectionName]: this.updateDisplayedSectionName,
      [ActiveSelectionPropertiesEnum.LabelPositionLeft]: this.updateLabelPosition,
      [ActiveSelectionPropertiesEnum.LabelPositionRight]: this.updateLabelPosition,
      [ActiveSelectionPropertiesEnum.Reverse]: this.updateReverse,
      [ActiveSelectionPropertiesEnum.RowLabel]: this.updateRowLabel,
      [ActiveSelectionPropertiesEnum.RowTableStartsAt]: this.updateRowTableStartsAt,
      [ActiveSelectionPropertiesEnum.SeatLabeling]: this.updateSeatLabeling,
      [ActiveSelectionPropertiesEnum.SectionName]: this.updateSectionName,
      [ActiveSelectionPropertiesEnum.StarstAt]: this.updateStartsAt,
      [ActiveSelectionPropertiesEnum.TableLabel]: this.updateTableLabel,
      [ActiveSelectionPropertiesEnum.SeatsLeft]: this.updateSeatsLeft,
      [ActiveSelectionPropertiesEnum.SeatsRight]: this.updateSeatsRight,
      [ActiveSelectionPropertiesEnum.SeatsUp]: this.updateSeatsUp,
      [ActiveSelectionPropertiesEnum.SeatsDown]: this.updateSeatsDown,
      [ActiveSelectionPropertiesEnum.RoundTableSeatsQuantity]: this.updateRoundTableSeatsQuantity,
      [ActiveSelectionPropertiesEnum.RowSeatsQuantity]: this.updateRowSeatsQuantity,
      [ActiveSelectionPropertiesEnum.RowCurve]: this.updateRowCurve,
      [ActiveSelectionPropertiesEnum.RowSeatSpacing]: this.updateRowSpacing,
      [ActiveSelectionPropertiesEnum.TableSelectionType]: this.updateTableSelectionType,
      [ActiveSelectionPropertiesEnum.TableRadius]: this.updateTableRadius,
      [ActiveSelectionPropertiesEnum.TableWidth]: this.updateTableSize,
      [ActiveSelectionPropertiesEnum.TableHeight]: this.updateTableSize,
      [ActiveSelectionPropertiesEnum.RoundTableEmptySpaces]: this.updateRounsTableEmptySpaces,
    };
  }

  public updateProperties = async (
    selectedItem: any,
    key: ActiveSelectionPropertiesEnum,
    value: any,
    allValues: any,
  ) => {
    // Row spacing moves the selection's children as a whole.
    if (key === ActiveSelectionPropertiesEnum.RowSpacing) {
      if (RowSpacingHelper.setSpacing(selectedItem, value)) {
        this.canvasHandler.canvas.requestRenderAll();
      }
      return;
    }

    const {
      properties: { rowSeatSize, categories: categoriesList },
    } = store.getState();

    const handler = this.handlers[key];

    if (!handler) {
      console.warn(`No handler for property: ${key}`);
      return;
    }

    const selectedItems = selectedItem.getObjects();

    const objects = selectedItems.filter((object: any) =>
      validTypes.includes(object.superType)
    );

    if (!objects.length) return;

    const rowQuantity = objects.filter(
      (object: any) => object.superType === ObjectSuperTypesEnum.Row
    ).length;

    const tablesQuantity = objects.filter((object: any) =>
      tableTypes.includes(object.superType)
    ).length;

    const selectedCategory =
      key === ActiveSelectionPropertiesEnum.Categories
        ? categoriesList.find((category: any) => category.key === value)
        : null;

    const affectsLabels = labelPropertyTypes.includes(key);

    const isTableLabeling =
      key === ActiveSelectionPropertiesEnum.TableLabel
        ? true
        : key === ActiveSelectionPropertiesEnum.RowLabel
          ? false
          : allValues?.[ActiveSelectionPropertiesEnum.TableLabel] !== undefined &&
            allValues?.[ActiveSelectionPropertiesEnum.RowLabel] === undefined
            ? true
            : tablesQuantity > 0 && rowQuantity === 0;

    const labelQuantity = isTableLabeling ? tablesQuantity : rowQuantity;

    const distributionKey = isTableLabeling
      ? allValues?.[ActiveSelectionPropertiesEnum.TableLabel]
      : allValues?.[ActiveSelectionPropertiesEnum.RowLabel];

    const startsAt = Math.max(
      1,
      parseInt(allValues?.[ActiveSelectionPropertiesEnum.RowTableStartsAt], 10) || 1
    );

    const labels = affectsLabels
      ? selectRowTableDistribution(distributionKey, labelQuantity, startsAt, false)
      : [];
    for (const [index, object] of objects.entries()) {
      object.setCoords();

      const childObjects = object.getObjects?.() ?? [];

      const seats = childObjects.filter(
        (child: any) => child.superType === ObjectSuperTypesEnum.Seat
      );

      const table = childObjects.filter(
        (child: any) => child.superType === ObjectSuperTypesEnum.Table
      );
      let orderedSeats = [];

      switch (object.superType) {
        case ObjectSuperTypesEnum.RectTable:
          orderedSeats = RectTableSeatHelper.getOrderedTableSeats({ seats, table: table[0] });
          break;
        case ObjectSuperTypesEnum.RoundTable:
          orderedSeats = RoundTableSeatHelper.getOrderedTableSeats({ seats, table: table[0] });
          break;
      }

      const textObjs = object.getObjects(ObjectTypesEnum.TextBox) || [];
      const textRight =
        textObjs.filter((textbox: any) => textbox.side === DirectionEnum.Right) || [];
      const textLeft =
        textObjs.filter((textbox: any) => textbox.side === DirectionEnum.Left) || [];


      const center = object.getCenterPoint();
      
      await handler.call(this, {
        value,
        allValues,
        selectedItem,
        object,
        selectedCategory,
        seats,
        table: table ? table[0] : table,
        rowSeatSize,
        index,
        key,
        rowQuantity,
        label: labels.length > 0 ? labels[index] : '',
        textRight,
        textLeft,
        orderedSeats
      });
      const changesRowLabel = object.superType === ObjectSuperTypesEnum.Row &&
        [...labelPropertyTypes, ActiveSelectionPropertiesEnum.LabelPositionLeft,
          ActiveSelectionPropertiesEnum.LabelPositionRight].includes(key);
      if (!changesRowLabel) object.setPositionByOrigin(center, 'center', 'center');
      object.setCoords();
    }
    this.canvasHandler.canvas.requestRenderAll();
  };

  private setSeatValue = (seat: any, objectData: any, seatlabel: string) => {
    const isTable = tableTypes.includes(objectData.superType);
    const sectionLabel = objectData.section.label;
    const rowOrTableLabel = isTable ? objectData.tableLabeling.label : objectData.row.label;
    seat.set({
      value: `${sectionLabel}${sectionLabel && 
      rowOrTableLabel &&
      "-"}${rowOrTableLabel}${(rowOrTableLabel ||
        sectionLabel) &&
        seatlabel &&
        "-"}${seatlabel}`,
      isLabeled: rowOrTableLabel !== "" && seatlabel !== "",
    });
  };

  private updateCategories = ({object, selectedCategory, seats, table}: any) => {
    object.set({
      category: {
        ...selectedCategory,
      },
    })
    const isTableSelection = object.selectionType && object.selectionType === SelectionTypeEnum.byTable;
    seats.forEach((seat: any) => {
      const seatShape = seat.item(0);
      seatShape.set({
        stroke: isTableSelection ? "#A1A1A1" : selectedCategory.color,
        fill: isTableSelection ? "#D1D1D1": `${selectedCategory.color}40`,
      });
      seat.set({
        category: {
          ...selectedCategory,
        },
      });
    });
    if(table){
      table.set({
        category: {
          ...selectedCategory,
        },
      });
      const tableShape = table.item(0);
      tableShape.set({
        stroke: isTableSelection ? selectedCategory.color : "#A1A1A1",
        fill: isTableSelection ? `${selectedCategory.color}40` : "#D1D1D1",
      });
    }
  }

  private updateSectionName = ({object, value, seats}: any) => {
    object.set({
      section: {
          label: value,
          displayedLabel: object.section.displayedLabel,
      },
    });
    if(object.superType === ObjectSuperTypesEnum.RectTable || object.superType === ObjectSuperTypesEnum.RoundTable){
      const tableGroup = object.getObjects().filter((obj: any) => obj.superType === ObjectSuperTypesEnum.Table)[0];
      tableGroup.set({
        value: `${value}${value && object.tableLabeling.label && "-"}${object.tableLabeling.label}`
      })
    }
    seats.forEach((seat: any) => {
      let txtObj = seat.item(1);
      this.setSeatValue(seat, object, txtObj.text);
      seat.set({
        sectionLabel: value
      })
    });
  }

  private updateDisplayedSectionName = ({object, value, seats}: any) => {
    object.set({
      section: {
          label: object.section.label,
          displayedLabel: value,
      },
    });
    seats.forEach((seat: any) => {
      let txtObj = seat.item(1);
      this.setSeatValue(seat, object, txtObj.text);
    });
  }

  private updateLabelPosition = ({object, seats, rowSeatSize, value, key}: any) => {
    if(object.superType !== ObjectSuperTypesEnum.Row) return;
    const side = key === ActiveSelectionPropertiesEnum.LabelPositionLeft ? DirectionEnum.Left : DirectionEnum.Right;
    if(value && object.row.position[side]) return;
    object.set({
      row: {
        ...object.row,
        enabled: value,
        position: {
          left:
            key === ActiveSelectionPropertiesEnum.LabelPositionLeft
              ? value
              : object.row.position.left,
          right:
            key === ActiveSelectionPropertiesEnum.LabelPositionRight
              ? value
              : object.row.position.right,
        },
      },
    });
    const {
      row: { displayedLabel: displayed, label: labl },
    } = object;
    const selectedText = displayed !== "" ? displayed : labl !== "" ? labl : "?";
    if (!value) {
      RowLabelHelper.removeRowLabel(object, side);

      object.setCoords();
      return;
    }

    const label = RowLabelHelper.createRowLabel({
      selectedItem: object,
      rowSeatSize,
      text: selectedText,
      side,
      labelOffset: this.LABEL_OFFSET,
      angle: object.angleHelp,
    });

    if (!label) return;

    RowLabelHelper.addRowLabel(object, label);

    object.setCoords();
  }

  private updateReverse = ({object, value, seats}: any) => {
    const {seat: {startsAt, key}} = object
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem: object,
      seats,
      distributionKey: key,
      startsAt,
      reverse: value,
      setSeatValue: this.setSeatValue,
      distributionObject: 'seat'
    });
  }

  private updateRowLabel = ({object, seats, rowSeatSize, label}: any) => {
    if(object.superType !== ObjectSuperTypesEnum.Row) return;
    const textObjs = object.getObjects(ObjectTypesEnum.TextBox) || [];
    const textRight =
    textObjs.filter((textbox: any) => textbox.side === DirectionEnum.Right) || [];
    const textLeft =
    textObjs.filter((textbox: any) => textbox.side === DirectionEnum.Left) || [];

    object.set({
      row: {
        ...object.row,
        label,
      },
    });
    seats.forEach((seat: any) => {
        this.setSeatValue(seat, object, seat.label);
        seat.set({
          rowLabel: label
        })
    });

    if (textLeft?.length) {
      RowLabelHelper.updateExistingRowLabel({
        selectedItem: object,
        label: textLeft[0],
        rowSeatSize,
        text: label,
        side: DirectionEnum.Left,
        labelOffset: this.LABEL_OFFSET,
        angle: RowLabelHelper.getReadableAngle(object.angleHelp || 0) - (object.angle || 0),
      });
    }

    if (textRight?.length) {
      RowLabelHelper.updateExistingRowLabel({
        selectedItem: object,
        label: textRight[0],
        rowSeatSize,
        text: label,
        side: DirectionEnum.Right,
        labelOffset: this.LABEL_OFFSET,
        angle: RowLabelHelper.getReadableAngle(object.angleHelp || 0) - (object.angle || 0),
      });
    }

    RowLabelHelper.updateBounds(object);
  }

  private updateRowTableStartsAt = (params: any) => {
    const { object } = params;
    if (object.superType === ObjectSuperTypesEnum.Row) {
      this.updateRowLabel(params);
      return;
    }
    if (tableTypes.includes(object.superType)) {
      this.updateTableLabel(params);
    }
  }

  private updateTableLabel = ({object, label, seats}: any) => {
    if(object.superType !== ObjectSuperTypesEnum.RectTable && object.superType !== ObjectSuperTypesEnum.RoundTable) return;
    const tableGroup = object.getObjects().filter((obj: any) => obj.superType === ObjectSuperTypesEnum.Table)[0];
    const text = tableGroup.item(1);
    object.set({tableLabeling: {
        label,
        displayedLabel: label,
        visible: true
    }});
    tableGroup.set({
      value: `${object.section.label}${object.section.label && label && "-"}${label}`
    })
    object.addWithUpdate();
    text.set({
      text: label,
      visible: true
    })
    seats.forEach((seat: any) => {
        this.setSeatValue(seat, object, seat.label);
        seat.set({
          tableLabel: label
        })
    });
  };

  private updateSeatLabeling = ({object, value, seats, orderedSeats, table }: any) => {
    const distributionObject = object.superType === ObjectSuperTypesEnum.Row ? 'seat' : 'seatLabeling';
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem: object,
      seats: orderedSeats.length === 0 ? seats : orderedSeats,
      distributionKey: value,
      startsAt: 1,
      reverse: false,
      setSeatValue: this.setSeatValue,
      distributionObject
    });
    if(table){
      table.set({
        seatLabeling: {
          ...object.seatLabeling
        }
      })
    }
  }

  private updateStartsAt = ({object, value, seats}: any) => {
    const distributionObject = object.superType === ObjectSuperTypesEnum.Row ? 'seat' : 'seatLabeling';
    const reverse = object?.seat?.reverse ?? false;
    const key = object?.[distributionObject]?.key;
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem: object,
      seats,
      distributionKey: key,
      startsAt: value,
      reverse,
      setSeatValue: this.setSeatValue,
      distributionObject
    });
  }


  private updateSeatsLeft = ({object, value}: any) => {
    this.updateRecTableSeats(object, value, DirectionEnum.Left)
  }

  private updateSeatsRight = ({object, value}: any) => {
    this.updateRecTableSeats(object, value, DirectionEnum.Right)
  }

  private updateSeatsUp = ({object, value}: any) => {
    this.updateRecTableSeats(object, value, DirectionEnum.Up)
  }

  private updateSeatsDown = ({object, value}: any) => {
    this.updateRecTableSeats(object, value, DirectionEnum.Down)
  }

  private updateRecTableSeats = async (rectTable: any, newQuantity: number, direction: DirectionEnum) => {
    const { tableGroup, table } = RectTableSeatHelper.getRectTableParts(rectTable);

    if (!tableGroup || !table) return;

    await RectTableSeatHelper.rebuildSeats({
      rectTable,
      table,
      tableGroup,
      newQuantity,
      direction,
      seatRadius: 7.5,
      seatGap: 8,
      setSeatValue: this.setSeatValue,
    });
  };
  
  private updateRoundTableSeatsQuantity = async ({object, value}: any) => {
    const { tableGroup, table } = RoundTableSeatHelper.getRoundTableParts(object);
    if (!tableGroup || !table) return;
    await RoundTableSeatHelper.rebuildSeats({
      roundTable: object,
      table,
      tableGroup,
      seatsQuantity: value,
      seatRadius: 7.5,
      seatGap: 8,
      setSeatValue: this.setSeatValue,
    });
  }

  private updateRowSeatsQuantity = async ({object, value, rowSeatSize, seats, textRight, textLeft}: any) => {
    await RowSeatHelper.updateSeatsQuantity({
      selectedItem: object,
      value,
      rowSeatSize,
      textRight,
      textLeft,
      seats,
      setSeatValue: this.setSeatValue,
      editable: this.canvasHandler.editable,
      container: this.canvasHandler.container.id,
      objectOption: this.canvasHandler.objectOption,
      labelOffset: this.LABEL_OFFSET,
    });
  }

  private updateRowCurve = async ({object, value, rowSeatSize, seats, textRight, textLeft}: any) => {
    RowSeatHelper.updateSeatsCurve({
      selectedItem: object,
      value,
      seats,
      rowSeatSize,
      textRight,
      textLeft,
      labelOffset: this.LABEL_OFFSET,
    });
  }
  
  private updateRowSpacing = async ({object, value, rowSeatSize, seats, textRight, textLeft}: any) => {
    RowSeatHelper.updateSeatsSpacing({
      selectedItem: object,
      value,
      seats,
      rowSeatSize,
      textRight,
      textLeft,
      labelOffset: this.LABEL_OFFSET,
    });
  }

  private updateTableSelectionType = async ({object, value, seats}: any) => {
    object.set({
      selectionType: value
    })
    const tableGroup = object.item(0);
    const table = tableGroup.item(0);
    const { category } = object;
    const isTableSelection = object.selectionType === SelectionTypeEnum.byTable;
    tableGroup.set({
      selectionType: value
    })
    table.set({
        fill: isTableSelection ? `${category.color}30` : "#D1D1D1",
        stroke: isTableSelection ? category.color : "#A1A1A1"
    })
    seats.forEach((seat: any) => {
        const shape = seat.item(0);
        shape.set({
            fill: isTableSelection ? "#D1D1D1" : `${category.color}40`,
            stroke: isTableSelection ? "#A1A1A1" : category.color
        });
        seat.set({
          selectionType: value
        })
    });
  }
  
  private updateTableRadius = async ({object, value}: any) => {
    const tableGroup = object.item(0);
    const table = tableGroup.item(0);
    table.set({
        radius: value
    });
    await RoundTableSeatHelper.rebuildSeats({
        roundTable: object,
        table,
        tableGroup,
        seatsQuantity: object.seatsQuantity,
        seatRadius: 7.5,
        seatGap: 8,
        setSeatValue: this.setSeatValue,
    });
  }

  private updateTableSize = ({object, value, key}: any) => {
    const tableGroup = object.item(0);
    const table = tableGroup.item(0);

    const dimension = key === ActiveSelectionPropertiesEnum.TableWidth ? 'width' : 'height';
    if (!object) return;

    const prevCenter = object.getCenterPoint();

    table.set({
        [dimension]: value
    });
    
    table.setCoords();

    tableGroup.addWithUpdate()
    
    const maxSeats = RectTableSeatHelper.calculateMaxSeats({table, seatRadius: 7.5});
    
    object.set({maxSeats});
    
    RectTableSeatHelper.repositionExistingSeats({
        rectTable: object, table, tableGroup, seatRadius: 7.5, seatGap: 8
    });
    
    object.addWithUpdate();
    object.setPositionByOrigin(prevCenter, "center", "center");
    object.setCoords();
  };

  private updateRounsTableEmptySpaces = async ({object, value}: any) => {
    const { tableGroup, table } = RoundTableSeatHelper.getRoundTableParts(object);
    object.set({
        emptySpaces: value,
        maxEmptySpaces: RoundTableSeatHelper.calculateMaxEmptySpaces(object.seatsQuantity, object.maxSeats)
    });
    await RoundTableSeatHelper.rebuildSeats({
        roundTable: object,
        table,
        tableGroup,
        seatsQuantity: object.seatsQuantity,
        seatRadius: 7.5,
        seatGap: 8,
        setSeatValue: this.setSeatValue,
        });  
  }

}

export default ActiveSelectionProperties;
